import { Field as FieldPrimitive } from '@base-ui/react/field';
import * as React from 'react';
import {
  type FieldProps,
  FieldTooltip,
  hasFieldProps,
  splitFieldProps,
} from '@/components/atoms/forms/_shared';
import {
  type InputState,
  inputStateClasses,
} from '@/components/atoms/forms/Input';
import {
  Field,
  FieldBottom,
  FieldBottomRight,
  FieldControl,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldTop,
  FieldTopRight,
} from '@/components/ui/form-field';
import { textareaBaseStyles } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

/** Visual state of the textarea (alias for InputState) */
export type TextareaState = InputState;

/**
 * Textarea renders a textarea control. Pass field props (label, error,
 * description, etc.) to automatically wrap in a Field. Without field props,
 * renders a bare textarea with zero overhead.
 *
 * @example
 * ```tsx
 * // Bare textarea
 * <Textarea placeholder="Enter message" />
 *
 * // With field props — auto-wraps in Field
 * <Textarea label="Message" error={errors.message} placeholder="Enter message" />
 *
 * // With icons
 * <Textarea startIcon={<MessageIcon />} placeholder="Message..." />
 * ```
 */
interface TextareaControlProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Visual state of the textarea (default, success, destructive) */
  state?: TextareaState;
  /** Icon displayed at the start (inline-start) of the textarea - respects RTL */
  startIcon?: React.ReactNode;
  /** Icon displayed at the end (inline-end) of the textarea - respects RTL */
  endIcon?: React.ReactNode;
  /** Whether to show an outline/border variant */
  hasOutline?: boolean;
}

export interface TextareaProps extends TextareaControlProps, FieldProps {}

// Use shared state classes from Input for consistency
const stateClasses = inputStateClasses;

const TextareaControl = React.forwardRef<
  HTMLTextAreaElement,
  TextareaControlProps
>(
  (
    {
      state = 'default',
      startIcon,
      endIcon,
      hasOutline,
      className,
      id,
      'aria-invalid': ariaInvalid,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref,
  ) => {
    const hasStartIcon = !!startIcon;
    const hasEndIcon = !!endIcon;
    const hasIcons = hasStartIcon || hasEndIcon;

    // Base textarea styles
    const textareaStyles = cn(
      textareaBaseStyles,
      stateClasses[state],
      hasOutline &&
        'border-border ring-1 ring-border focus-visible:ring-2 focus-visible:ring-ring',
      hasStartIcon && 'ps-10',
      hasEndIcon && 'pe-10',
    );

    // Simple textarea without icons - textarea is the root element
    if (!hasIcons) {
      return (
        <FieldPrimitive.Control
          ref={ref}
          // Field.Control is input-typed; its render element is a textarea.
          // Route native props through it so computed state wins and handlers compose.
          {...(props as FieldPrimitive.Control.Props)}
          id={id}
          data-slot="textarea"
          data-state={state}
          aria-invalid={ariaInvalid ?? (state === 'destructive' || undefined)}
          aria-describedby={ariaDescribedBy}
          className={cn(textareaStyles, className)}
          render={<textarea />}
        />
      );
    }

    // Textarea with icons - wrapper div is the root element
    return (
      <div
        data-slot="textarea-wrapper"
        className={cn('group/textarea relative', className)}
      >
        {/* Start icon (top-start corner) */}
        {hasStartIcon && (
          <span
            aria-hidden="true"
            className="absolute start-3 top-2.5 text-text-quaternary pointer-events-none transition-colors duration-200 ease-out motion-reduce:transition-none [&>svg]:size-5 group-focus-within/textarea:text-muted-foreground"
          >
            {startIcon}
          </span>
        )}

        <FieldPrimitive.Control
          ref={ref}
          {...(props as FieldPrimitive.Control.Props)}
          id={id}
          data-slot="textarea"
          data-state={state}
          aria-invalid={ariaInvalid ?? (state === 'destructive' || undefined)}
          aria-describedby={ariaDescribedBy}
          className={textareaStyles}
          render={<textarea />}
        />

        {/* End icon (top-end corner) */}
        {hasEndIcon && (
          <span
            aria-hidden="true"
            className="absolute end-3 top-2.5 text-text-quaternary pointer-events-none transition-colors duration-200 ease-out motion-reduce:transition-none [&>svg]:size-5 group-focus-within/textarea:text-muted-foreground"
          >
            {endIcon}
          </span>
        )}
      </div>
    );
  },
);

TextareaControl.displayName = 'TextareaControl';

// =============================================================================
// Textarea — public API with smart field mode
// =============================================================================

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (props, ref) => {
    const [fieldPropsObj, controlProps] = splitFieldProps(props);

    if (!hasFieldProps(fieldPropsObj)) {
      return (
        <TextareaControl
          ref={ref}
          {...(controlProps as TextareaControlProps)}
          required={fieldPropsObj.required}
        />
      );
    }

    const {
      label,
      required,
      labelIcon,
      tooltip,
      topRight,
      description,
      bottomRight,
      error,
      fieldClassName,
    } = fieldPropsObj;
    const cp = controlProps as TextareaControlProps;
    const resolvedState = error ? 'destructive' : cp.state;
    const hasTopRow = label || topRight;
    const hasBottomRow = description || bottomRight || error;

    return (
      <Field
        state={resolvedState}
        disabled={cp.disabled}
        className={fieldClassName}
      >
        {hasTopRow && (
          <FieldTop>
            {label && (
              <FieldLabel required={required} labelIcon={labelIcon}>
                {label}
                {tooltip && <FieldTooltip content={tooltip} />}
              </FieldLabel>
            )}
            {topRight && <FieldTopRight>{topRight}</FieldTopRight>}
          </FieldTop>
        )}
        <FieldControl>
          {(ctrlProps) => (
            <TextareaControl
              ref={ref}
              {...cp}
              {...ctrlProps}
              required={required}
              state={resolvedState}
            />
          )}
        </FieldControl>
        {hasBottomRow && (
          <FieldBottom>
            {error && <FieldError>{error}</FieldError>}
            {!error && description && (
              <FieldDescription>{description}</FieldDescription>
            )}
            {bottomRight && (
              <FieldBottomRight className="ms-auto">
                {bottomRight}
              </FieldBottomRight>
            )}
          </FieldBottom>
        )}
      </Field>
    );
  },
);
Textarea.displayName = 'Textarea';

export { Textarea };
